import {useAction} from '../contexts/ActionContext'
import {usePath} from '../contexts/PathContext'
import {useRefInfo} from '../contexts/RefInfoContext'
import type {DirectoryItem} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {repositoryTreePath, type RepositoryTreePathAction} from '@github-ui/paths'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import React, {useMemo} from 'react'

interface UrlOptions {
  absolute?: boolean
  action?: RepositoryTreePathAction
  commitish?: string
  params?: string
  path?: string
  hash?: string
}

function getTail({params, hash}: UrlOptions, isSSR: boolean) {
  const urlParams = params ? `?${params}` : ''
  let hashStr = ''
  if (!isSSR) {
    if (hash === undefined) {
      hashStr = window.location.hash
    } else {
      hashStr = hash ? `#${hash}` : ''
    }
  }
  return urlParams + hashStr
}

export function useUrlCreator() {
  const {name: repoName, ownerLogin} = useCurrentRepository()
  const currentPath = usePath()
  const action = useAction()
  const refInfo = useRefInfo()
  const {isServer: isSSR} = useRuntimeEnvironment()

  const getItemUrl = React.useCallback(
    (item: DirectoryItem) => {
      return repositoryTreePath({
        repo: {
          name: repoName,
          ownerLogin,
        },
        commitish: refInfo.name,
        action: item.contentType === 'directory' ? 'tree' : 'blob',
        path: item.path,
      })
    },
    [ownerLogin, repoName, refInfo.name],
  )

  const getUrl = React.useCallback(
    (options: UrlOptions = {}) => {
      const url =
        repositoryTreePath({
          repo: {
            name: repoName,
            ownerLogin,
          },
          commitish: options.commitish || refInfo.name,
          action: options.action || action,
          path: options.path || currentPath,
        }) + getTail(options, isSSR)

      if (options.absolute) {
        return new URL(url, window.location.origin).href
      }
      return url
    },
    [ownerLogin, repoName, refInfo.name, action, currentPath, isSSR],
  )

  const createPermalink = React.useCallback(
    (options: UrlOptions = {}) => {
      const url =
        repositoryTreePath({
          repo: {
            name: repoName,
            ownerLogin,
          },
          commitish: refInfo.currentOid,
          action: options.action || action,
          path: options.path || currentPath,
        }) + getTail(options, isSSR)

      if (options.absolute) {
        return new URL(url, window.location.origin).href
      }
      return url
    },
    [ownerLogin, repoName, refInfo.currentOid, action, currentPath, isSSR],
  )

  const isCurrentPagePermalink = React.useCallback(() => {
    if (isSSR) return false
    // ancestry references have an OID name
    // check if the OID is in the path to see if it's a permalink
    return refInfo.name === refInfo.currentOid && window.location.pathname.includes(refInfo.currentOid)
  }, [isSSR, refInfo.name, refInfo.currentOid])

  return useMemo(
    () => ({
      getItemUrl,
      getUrl,
      createPermalink,
      isCurrentPagePermalink,
    }),
    [getItemUrl, getUrl, createPermalink, isCurrentPagePermalink],
  )
}
